import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  ArrowUp,
  ArrowRight,
  Plus,
  ChevronRight,
  MoreVertical,
  Maximize,
  Minimize,
  BarChart3,
  TrendingDown,
  Lightbulb,
  MessageSquareText,
  Target,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";
import { playMessageCompleteSound } from "../../assets/message-complete";
import { useSelector } from "react-redux";
import Button from "../Button/Button";
import { surveyChat, get, post, patch, del } from "../../serverComm";
import { Response as AIResponse } from "../ai-elements/response";
import ConfirmationModal from "../ui/ConfirmationModal";
import { useNavigate } from "react-router-dom";

const AgentAvatar = ({ size = 28 }) => (
  <img
    src="/images/pollBoltIconCyan.svg"
    alt=""
    className="rounded-full flex-shrink-0"
    style={{ width: size, height: size }}
  />
);

const ThinkingDots = () => (
  <div className="flex items-center gap-2.5 pb-3">
    <AgentAvatar size={28} />
    <span className="text-[13px] font-medium thinking-shimmer">Analyzing your data</span>
  </div>
);

const messageStyles = {
  fontSize: '1rem',
  lineHeight: '1.5',
  whiteSpace: 'pre-line',
};

const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const EMOJI_MAP = {
  ':sparkles:': '✨', ':bulb:': '💡', ':chart:': '📊', ':graph:': '📈',
  ':idea:': '💭', ':check:': '✅', ':fire:': '🔥', ':rocket:': '🚀',
  ':star:': '⭐', ':warning:': '⚠️', ':info:': 'ℹ️', ':question:': '❓',
  ':thumbsup:': '👍', ':clap:': '👏',
};
const EMOJI_RE = /:(sparkles|bulb|chart|graph|idea|check|fire|rocket|star|warning|info|question|thumbsup|clap):/g;
const replaceEmojis = (text) => text.replace(EMOJI_RE, (m) => EMOJI_MAP[m] || m);

const formatText = (text) => {
  if (!text) return '';
  const escaped = escapeHtml(text);
  const withBold = escaped.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  return replaceEmojis(withBold);
};

const FormattedMessage = ({ content }) => (
  <div
    className="message-content typewriter-text"
    style={{ ...messageStyles, color: 'inherit' }}
    dangerouslySetInnerHTML={{ __html: formatText(content) }}
  />
);

const parseBoldSegments = (content) => {
  const segments = [];
  let lastIndex = 0;
  let boldStart = -1;

  for (let i = 0; i < content.length; i++) {
    if (i < content.length - 1 && content[i] === '*' && content[i + 1] === '*') {
      if (boldStart === -1) {
        if (i > lastIndex) segments.push({ text: content.substring(lastIndex, i), bold: false });
        boldStart = i + 2;
        i++;
      } else {
        segments.push({ text: content.substring(boldStart, i), bold: true });
        lastIndex = i + 2;
        boldStart = -1;
        i++;
      }
    }
  }
  if (lastIndex < content.length) segments.push({ text: content.substring(lastIndex), bold: false });
  return segments;
};

const TypewriterText = ({ content, onComplete, scrollToBottom }) => {
  const segments = useMemo(() => parseBoldSegments(content), [content]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const speed = 8;

  useEffect(() => {
    if (isComplete) return;

    if (currentSegment < segments.length) {
      const segment = segments[currentSegment];
      if (currentChar < segment.text.length) {
        const timer = setTimeout(() => {
          setCurrentChar(c => c + 1);
          if (typeof scrollToBottom === 'function' && currentChar % 10 === 0) {
            scrollToBottom(true);
          }
        }, speed);
        return () => clearTimeout(timer);
      } else {
        setCurrentSegment(s => s + 1);
        setCurrentChar(0);
      }
    } else if (!isComplete) {
      playMessageCompleteSound();
      setIsComplete(true);
      onComplete?.();
    }
  }, [segments, currentSegment, currentChar, isComplete, onComplete, scrollToBottom]);

  if (isComplete || (currentSegment >= segments.length)) {
    return <FormattedMessage content={content} />;
  }

  const result = [];
  for (let i = 0; i < currentSegment; i++) {
    const seg = segments[i];
    result.push(seg.bold ? <strong key={i}>{replaceEmojis(seg.text)}</strong> : <span key={i}>{replaceEmojis(seg.text)}</span>);
  }
  if (currentSegment < segments.length) {
    const partialText = replaceEmojis(segments[currentSegment].text.substring(0, currentChar));
    result.push(segments[currentSegment].bold ? <strong key="cur">{partialText}</strong> : <span key="cur">{partialText}</span>);
  }

  return (
    <div className="typewriter-text" style={messageStyles}>
      {result}
      <span className="cursor inline-block w-1 h-4 ml-0.5 bg-neutral-900 animate-pulse" />
    </div>
  );
};

const INITIAL_SUGGESTIONS = [
  { label: "Summarize key findings", icon: BarChart3 },
  { label: "Where are users dropping off?", icon: TrendingDown },
  { label: "Find patterns between answers", icon: Search },
  { label: "Analyze open-ended responses", icon: MessageSquareText },
  { label: "Suggest business actions", icon: Target },
  { label: "What should I improve first?", icon: Lightbulb },
];

const SurveyResearchAssistant = ({ surveyId, survey }) => {
  const navigate = useNavigate();
  const { currentSubscription } = useSelector((state) => state.app);
  const [question, setQuestion] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, convoId: null });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const creatingConversationRef = useRef(null);
  const audioCtxRef = useRef(null);
  const submitInProgressRef = useRef(false);

  const scrollToBottom = useCallback((instant = false) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: instant ? 'instant' : 'smooth',
      block: 'nearest',
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
    const t = setTimeout(scrollToBottom, 300);
    return () => clearTimeout(t);
  }, [chatMessages, typingMessageId, scrollToBottom]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      const el = textareaRef.current || inputRef.current;
      if (!el) return;
      el.focus();
      if (el.value) {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      if ((isMac ? e.metaKey : e.ctrlKey) && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        createNewConversation();
      }
      if (e.key === 'Escape' && isFullscreen) {
        e.preventDefault();
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen]);

  useEffect(() => {
    const onChip = (evt) => {
      try {
        const text = String(evt.detail || '');
        setQuestion(prev => {
          const cur = prev.trim();
          return cur ? `${cur} ${text}` : text;
        });
        focusInput();
      } catch {}
    };
    window.addEventListener('ai-followup-chip', onChip);
    return () => window.removeEventListener('ai-followup-chip', onChip);
  }, [focusInput]);

  useEffect(() => {
    const close = () => {
      if (menuOpenId !== null) setMenuOpenId(null);
      if (isSidebarOpen) setIsSidebarOpen(false);
    };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menuOpenId, isSidebarOpen]);

  useEffect(() => {
    (async () => {
      try {
        const list = await get(`/api/conversations?surveyId=${encodeURIComponent(surveyId)}`);
        setConversations(list.conversations || []);
      } catch {}
    })();
  }, [surveyId]);

  const openConversation = useCallback(async (conversationId) => {
    try {
      setActiveConversationId(conversationId);
      const data = await get(`/api/conversations/${conversationId}`);
      const loadedMessages = (data.messages || []).map((m) => ({
        id: m._id || String(m.createdAt),
        type: m.role === 'user' ? 'user' : 'assistant',
        content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content),
        timestamp: m.createdAt,
      }));
      setChatMessages(loadedMessages);
      setShowChat(true);
      focusInput();
      if (!data?.conversation?.title) {
        const firstUser = loadedMessages.find((m) => m.type === 'user' && m.content);
        if (firstUser) {
          const title = String(firstUser.content).trim().slice(0, 50);
          if (title) {
            try {
              await patch(`/api/conversations/${conversationId}`, { title });
              const list = await get(`/api/conversations?surveyId=${encodeURIComponent(surveyId)}`);
              setConversations(list.conversations || []);
            } catch {}
          }
        }
      }
    } catch {}
  }, [surveyId, focusInput]);

  const createNewConversation = useCallback(async () => {
    setChatMessages([]);
    setShowChat(false);
    setActiveConversationId(null);
    focusInput();
  }, [focusInput]);

  const ensureConversation = useCallback(async () => {
    if (activeConversationId) return activeConversationId;
    if (!creatingConversationRef.current) {
      creatingConversationRef.current = post("/api/conversations", { surveyId });
    }
    try {
      const res = await creatingConversationRef.current;
      creatingConversationRef.current = null;
      setActiveConversationId(res.id);
      return res.id;
    } catch (e) {
      creatingConversationRef.current = null;
      throw e;
    }
  }, [activeConversationId, surveyId]);

  const unlockAudio = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start();
      audioCtxRef.current = ctx;
    } catch {}
  }, []);

  const doSubmit = useCallback(async (q) => {
    if (!q || isLoading || submitInProgressRef.current) return;
    submitInProgressRef.current = true;

    unlockAudio();

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: q,
      timestamp: new Date().toISOString(),
    };

    const isFirstMessage = chatMessages.length === 0;
    setChatMessages(prev => [...prev, userMessage]);
    setShowChat(true);
    setIsLoading(true);
    setQuestion("");

    if (isFirstMessage) setIsFullscreen(true);

    try {
      const convoId = await ensureConversation();

      if (isFirstMessage) {
        const title = q.slice(0, 50);
        try { await patch(`/api/conversations/${convoId}`, { title }); } catch {}
        try {
          const list = await get(`/api/conversations?surveyId=${encodeURIComponent(surveyId)}`);
          setConversations(list.conversations || []);
        } catch {}
      }

      const history = [...chatMessages, userMessage];
      const response = await surveyChat({
        surveyId,
        conversationId: convoId,
        memoryEnabled: true,
        messages: history.map((m) => ({ type: m.type, content: m.content })),
      });

      const agentMessageId = Date.now().toString();
      setChatMessages(prev => [...prev, {
        id: agentMessageId,
        type: 'assistant',
        content: response?.answer || "I couldn't generate an answer.",
        timestamp: new Date().toISOString(),
      }]);
      setTypingMessageId(agentMessageId);
      setTimeout(() => scrollToBottom(true), 100);

      try {
        const list = await get(`/api/conversations?surveyId=${encodeURIComponent(surveyId)}`);
        setConversations(list.conversations || []);
      } catch {}
    } catch {
      setChatMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'assistant',
        content: "Sorry, I ran into an error while analyzing the survey.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
      submitInProgressRef.current = false;
      focusInput();
    }
  }, [isLoading, chatMessages, ensureConversation, surveyId, scrollToBottom, focusInput, unlockAudio]);

  const handleSubmit = useCallback((e) => {
    e?.preventDefault?.();
    const q = question.trim();
    if (q) doSubmit(q);
  }, [question, doSubmit]);

  const sendToAgent = useCallback((text) => {
    const q = text.trim();
    if (q) doSubmit(q);
  }, [doSubmit]);

  const handleDelete = useCallback(async () => {
    if (!confirmDelete.convoId) return;
    try {
      await del(`/api/conversations/${confirmDelete.convoId}`);
      if (activeConversationId === confirmDelete.convoId) {
        setActiveConversationId(null);
        setChatMessages([]);
        setShowChat(false);
      }
      const list = await get(`/api/conversations?surveyId=${encodeURIComponent(surveyId)}`);
      setConversations(list.conversations || []);
    } catch {
      toast.error('Failed to delete conversation');
    } finally {
      setConfirmDelete({ open: false, convoId: null });
    }
  }, [confirmDelete.convoId, activeConversationId, surveyId]);

  if (currentSubscription && currentSubscription.plan?.type === "free") {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-white relative">
        <div
          className="absolute top-0 left-0 bottom-0 right-0 backdrop-blur-sm bg-white/30 flex justify-center"
          style={{ zIndex: 12 }}
        >
          <div className="text-center mt-40">
            <h2 className="text-3xl mb-4">Unlock AI Research Assistant</h2>
            <p className="text-sm mb-6 max-w-[400px]">
              Get AI-powered insights and analysis of your survey responses on paid plans.
            </p>
            <Button
              size="sm"
              endIcon={<ArrowRight size={16} />}
              className="text-xs text-app-black px-6 rounded-xl bg-[#FEFD7D] hover:bg-[#ffff93]"
              onClick={() => navigate('/profile?tab=billing')}
            >
              Upgrade Now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const hasConversation = showChat && chatMessages.length > 0;

  const containerClass = isFullscreen
    ? "fixed inset-0 z-[9999] bg-white flex flex-col"
    : "bg-white h-full flex flex-col overflow-hidden -mt-[calc(var(--navbar-height,64px)+3.5rem)] md:-mt-[calc(var(--navbar-height,64px)+3.5rem)]";
  const containerStyle = isFullscreen ? undefined : { height: 'calc(100vh - var(--navbar-height, 64px))' };

  return (
    <>
      <style>{`
        @keyframes thinkingShimmer {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .thinking-shimmer {
          animation: thinkingShimmer 1.5s ease-in-out infinite;
        }
        @keyframes chipIn {
          from { opacity: 0; transform: scale(0.92); filter: blur(4px); }
          to { opacity: 1; transform: scale(1); filter: blur(0); }
        }
        .animate-chip-in {
          animation: chipIn 0.3s ease-out both;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.4s ease-out both;
        }
        .typewriter-text, .message-content { font-size: 1rem; line-height: 1.5; white-space: pre-line; }
        .typewriter-text strong, .message-content strong { font-weight: 600; }
      `}</style>

      <div className={containerClass} style={containerStyle}>
        {/* Toolbar */}
        <div className={`flex-shrink-0 bg-white px-4 sm:px-6 py-2.5 border-b border-neutral-100 ${isFullscreen ? '' : 'z-20'}`}>
          <div className={`flex items-center justify-between ${isFullscreen ? 'max-w-3xl mx-auto' : ''}`}>
            <div className="flex items-center gap-2">
              <button
                onClick={createNewConversation}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 active:bg-neutral-950 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                New Chat
              </button>
              {conversations.length > 0 && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsSidebarOpen(!isSidebarOpen); }}
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 rounded-full border border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300 transition-colors"
                  >
                    History ({conversations.length})
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isSidebarOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {isSidebarOpen && (
                    <div className="absolute top-full left-0 mt-2 w-72 max-h-80 overflow-y-auto bg-white border border-neutral-200 rounded-2xl shadow-lg z-50" onClick={(e) => e.stopPropagation()}>
                      {conversations.map((c) => (
                        <div key={c.id} className="relative group">
                          <button
                            onClick={() => { openConversation(c.id); setIsSidebarOpen(false); }}
                            className={`w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors ${activeConversationId === c.id ? 'bg-neutral-50' : ''}`}
                          >
                            <div className="truncate font-medium text-neutral-800 pr-6">{c.title || 'Untitled'}</div>
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === c.id ? null : c.id); }}
                            className="opacity-0 group-hover:opacity-100 absolute right-2 top-3 p-1 rounded-full hover:bg-neutral-100 text-neutral-400 transition-all"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {menuOpenId === c.id && (
                            <div className="absolute right-2 top-8 z-50 w-24 bg-white border border-neutral-200 rounded-xl shadow-md overflow-hidden">
                              <button
                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                onClick={() => { setMenuOpenId(null); setConfirmDelete({ open: true, convoId: c.id }); }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-neutral-200 text-neutral-500 hover:bg-neutral-50 hover:border-neutral-300 transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Conversation area */}
        <div className="flex-1 overflow-y-auto">
          <div className={`max-w-3xl mx-auto w-full px-4 sm:px-6 ${isFullscreen ? 'flex flex-col' : ''}`}>

            {/* Empty state */}
            {!hasConversation && (
              <div className="flex flex-col justify-end sm:justify-center min-h-[60vh] sm:min-h-[50vh] pb-4 sm:pb-0">
                <div className="mb-8 animate-fade-in">
                  <h1 className="text-3xl sm:text-[2.1rem] font-medium tracking-tight text-neutral-900 leading-tight mb-1">
                    What would you like to understand?
                  </h1>
                  <p className="text-lg sm:text-xl text-neutral-400 font-normal leading-snug">
                    Ask about trends, drop-off, response patterns, or recommendations.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {INITIAL_SUGGESTIONS.map((s, idx) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.label}
                        onClick={() => sendToAgent(s.label)}
                        className="animate-chip-in inline-flex items-center gap-2 text-[14px] px-4 py-2.5 rounded-full border border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300 active:bg-neutral-100 transition-all"
                        style={{ animationDelay: `${idx * 0.04}s` }}
                      >
                        <Icon size={15} className="text-neutral-400" />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Messages */}
            {hasConversation && (
              <div className="pt-8 pb-4">
                {chatMessages.map((m) => {
                  const isAssistant = m.type !== 'user';
                  const isVegaJson = typeof m.content === 'string' && /"type"\s*:\s*"vega-lite"/i.test(m.content);
                  const showTypewriter = isAssistant && typingMessageId === m.id && !isVegaJson;

                  if (m.type === 'user') {
                    return (
                      <div key={m.id} className="flex w-full flex-col items-end gap-0.5 pb-3">
                        <div className="bg-neutral-100 rounded-3xl px-5 py-2.5 max-w-[70%]">
                          <p className="text-[14px] text-neutral-900 leading-relaxed whitespace-pre-wrap">{m.content}</p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={m.id} className="flex w-full flex-col gap-2 pb-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <AgentAvatar size={28} />
                        </div>
                        <div className="flex-1 min-w-0 pt-1">
                          {showTypewriter ? (
                            <TypewriterText
                              content={m.content}
                              onComplete={() => setTypingMessageId(null)}
                              scrollToBottom={scrollToBottom}
                            />
                          ) : (
                            <div className="text-[14px] text-neutral-800 leading-relaxed">
                              <AIResponse>{m.content}</AIResponse>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {isLoading && <ThinkingDots />}
                <div ref={messagesEndRef} className={isFullscreen ? "h-32" : "h-8"} />
              </div>
            )}
          </div>
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 px-2 sm:px-4 pb-3 sm:pb-4 bg-white">
          <div className="max-w-3xl mx-auto w-full">
            <div
              className="relative rounded-2xl border border-neutral-200/80 bg-white/80 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] focus-within:border-neutral-300 focus-within:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-all duration-200"
              onClick={() => textareaRef.current?.focus()}
            >
              <textarea
                ref={textareaRef}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={onKeyDown}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                }}
                placeholder={hasConversation ? "Ask a follow-up question..." : "Ask anything about your survey data..."}
                className="w-full bg-transparent resize-none focus:outline-none text-[15px] text-neutral-900 placeholder:text-neutral-400 px-4 pt-3 pb-2 leading-[1.4]"
                rows={1}
                dir="auto"
                disabled={isLoading}
                style={{ minHeight: '44px', maxHeight: '200px' }}
              />
              <div className="flex items-center justify-end px-2 pb-2">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!question.trim() || isLoading}
                  className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors ${!question.trim() || isLoading ? 'bg-neutral-100 text-neutral-400' : 'bg-neutral-900 text-white hover:bg-neutral-800'}`}
                  title="Send"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="text-center text-[11px] text-neutral-400 mt-2">Press Enter to send · Shift+Enter for new line</div>
          </div>
        </div>

        <ConfirmationModal
          isOpen={confirmDelete.open}
          onClose={() => setConfirmDelete({ open: false, convoId: null })}
          onConfirm={handleDelete}
          title="Delete Conversation"
          message="Are you sure you want to delete this conversation? This action cannot be undone."
        />
      </div>
    </>
  );
};

export default SurveyResearchAssistant;
